import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import SiteHeader from "@/components/SiteHeader";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, RefreshCw, Pencil, Sparkles, Check, X } from "lucide-react";

const TONE_OPTIONS = [
  "Heartfelt","Funny","Nostalgic","Emotional","Warm and grateful","Light and playful","Inspirational","Poetic","Simple and honest","Premium and elegant","Mix of funny + emotional"
];

export default function PreviewPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {section, text}
  const [toneOverride, setToneOverride] = useState("");
  const [instruction, setInstruction] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, b] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/generated/${projectId}`).catch(() => ({ data: null })),
      ]);
      setProject(p.data);
      setBook(b.data);
      setBusy(p.data.draft_status === "generating");
      setLoadError(false);
      setNotFound(false);
    } catch (e) {
      setLoadError(true);
      setNotFound(e.response?.status === 404);
      if (e.response?.status !== 404) toast.error("Could not load preview");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (project?.draft_status !== "generating" && !["queued", "generating"].includes(book?.images_status)) return;
    const interval = setInterval(load, 4000);
    return () => clearInterval(interval);
  }, [project?.draft_status, book?.images_status, load]);

  const regenerateAll = async (extra = "") => {
    setBusy(true);
    toast.info("Re-writing the draft…");
    try {
      await api.post("/generate/async", {
        project_id: projectId,
        tone_override: toneOverride || undefined,
        instruction: extra || instruction || undefined,
      });
      setProject(p => ({ ...p, draft_status: "generating" }));
      toast.success("Your draft is being rewritten. You can return to this page later.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate.");
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const { data } = await api.put(`/generated/${projectId}/edit`, { project_id: projectId, section: editing.section, new_text: editing.text });
      setBook(data);
      toast.success("Saved.");
      setEditing(null);
    } catch (e) { toast.error("Could not save edit"); }
  };

  if (loading) return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="font-serif text-2xl text-ink">Loading your draft…</p>
      </div>
    </div>
  );

  if (notFound) return <><SiteHeader/><main id="main-content" className="form-shell"><h1 className="font-serif text-3xl" data-testid="draft-not-found-title">This book is no longer available.</h1><p className="text-sm text-inkMute mt-4" data-testid="draft-not-found-message">The project linked to this address could not be found. Your other saved books are still available. If you placed an order for this book, contact us with your order ID.</p><div className="flex flex-wrap gap-4 mt-7"><Link className="btn-primary" to="/projects" data-testid="missing-draft-your-books">Your books</Link><Link className="btn-ghost" to="/contact" data-testid="missing-draft-contact">Contact us</Link></div></main></>;

  if (!book) return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-serif text-3xl text-ink" data-testid="draft-empty-heading">{loadError ? "We couldn’t load this book." : busy ? "Your story is taking shape." : project?.draft_status === "failed" ? "Your draft needs another try." : "Your next chapter is on its way."}</h1>
        <p className="text-sm text-inkMute mt-3" role="status" data-testid="draft-empty-status">{loadError ? "Please try again." : busy ? "Writing and illustrations can take several minutes. You can return from Your books." : project?.draft_status === "failed" ? "Generation didn’t finish. Retry below or contact us for help." : "Draft generation begins after ordering. You can keep refining your memories in the meantime."}</p>
        <button onClick={() => navigate(`/create/${projectId}`)} className="btn-primary mt-6" data-testid="back-to-create">Open questionnaire</button>
        {project?.draft_status === "failed" && <button onClick={() => regenerateAll()} disabled={busy} className="btn-ghost mt-6 ml-3" data-testid="retry-generation">Retry generation</button>}
        {loadError && <button onClick={load} className="btn-ghost mt-6 ml-3" data-testid="retry-preview-load">Try again</button>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader showNav={false} />
      <main id="main-content" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Book preview */}
          <div className="lg:flex-1 min-w-0">
            <p className="k-overline mb-3">Preview · {project?.title}</p>
            <h1 className="page-title" data-testid="draft-ready-heading">Your draft is ready.</h1>
            <p className="text-sm text-inkMute mt-2" data-testid="draft-review-notice">This is your written draft, not a final print proof. Contact us about print approval and changes.</p>
            {busy && <p className="policy-notice mt-5" role="status" data-testid="draft-generating-status">Your new draft is being written. The previous version stays visible until it’s ready. Regeneration replaces the full draft.</p>}
            <p className="text-xs text-inkMute mt-3" data-testid="illustration-status">Illustrations: {book.images_status || "not requested"}{book.images_total ? ` · ${book.images_done || 0} of ${book.images_total} processed` : ""}</p>
            {["partial", "failed"].includes(book.images_status) && <p className="policy-notice mt-5" role="status" data-testid="illustration-service-notice">Some illustrations couldn’t be completed. Your written draft is saved. Please <Link to="/contact" className="underline" data-testid="illustration-support-link">contact us</Link> before regenerating the book.</p>}

            {/* Cover */}
            <div className="mt-8 k-book-page text-center">
              <p className="k-overline mb-3">Cover</p>
              <EditableHeader value={book.title} editing={editing} setEditing={setEditing} section="title" testId="edit-title">
                <h2 className="font-serif text-3xl sm:text-4xl text-ink leading-tight">{book.title}</h2>
              </EditableHeader>
              <EditableHeader value={book.subtitle} editing={editing} setEditing={setEditing} section="subtitle" testId="edit-subtitle">
                <p className="font-serif italic text-inkMute mt-2">{book.subtitle}</p>
              </EditableHeader>
              <div className="mt-8 text-xs text-inkMute">{book.cover_style_suggestion}</div>
            </div>

            {/* Back cover */}
            <div className="mt-6 k-book-page">
              <p className="k-overline mb-3">Back cover</p>
              <EditableText value={book.back_cover_blurb} editing={editing} setEditing={setEditing} section="back_cover_blurb" onSave={saveEdit}>
                <p className="font-serif text-ink leading-relaxed">{book.back_cover_blurb}</p>
              </EditableText>
            </div>

            {/* Dedication */}
            <div className="mt-6 k-book-page">
              <p className="k-overline mb-3">Dedication</p>
              <EditableText value={book.dedication} editing={editing} setEditing={setEditing} section="dedication" onSave={saveEdit}>
                <p className="font-serif text-lg italic text-ink leading-relaxed">{book.dedication}</p>
              </EditableText>
            </div>

            {/* Chapter outline */}
            <div className="mt-6 k-book-page">
              <p className="k-overline mb-3">Chapter outline</p>
              <ol className="space-y-3">
                {(book.chapter_outline || []).map((c, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="font-serif text-chocolate w-8 shrink-0">{String(i+1).padStart(2,'0')}</span>
                    <div className="flex-1">
                      <EditableHeader value={c.title} editing={editing} setEditing={setEditing} section={`chapter:${i}`}>
                        <p className="font-serif text-lg text-ink">{c.title}</p>
                      </EditableHeader>
                      <p className="text-sm text-inkMute mt-0.5">{c.summary}</p>
                      <button disabled={busy} onClick={() => regenerateAll(`Rewrite chapter "${c.title}" with stronger personal detail and the chosen tone.`)} className="text-xs text-chocolate hover:underline inline-flex items-center gap-1 mt-2" data-testid={`regen-chapter-${i}`}>
                        <RefreshCw size={12} /> Rewrite draft with focus on this chapter
                      </button>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {(book.chapters || []).map((chapter, i) => <section className="k-book-page mt-6" key={i} data-testid={`preview-chapter-${i}`}><p className="k-overline mb-3">Chapter {i + 1}</p><h2 className="font-serif text-2xl mb-5">{chapter.title}</h2><EditableText value={chapter.body} setEditing={setEditing} section={`chapter_body:${i}`}><p className="whitespace-pre-wrap text-sm leading-loose" data-testid={`chapter-body-${i}`}>{chapter.body}</p></EditableText><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">{(chapter.images || []).filter(img => img.status === "ready" && img.photo_id).map(img => <img key={img.photo_id} src={`${API}/photos/${img.photo_id}`} alt={`AI illustration for ${chapter.title}`} loading="lazy" className="w-full object-contain rounded"/>)}</div>{(chapter.direct_photo_ids || []).map(id => <img key={id} src={`${API}/photos/${id}`} alt={`Your photograph included in ${chapter.title}`} loading="lazy" className="w-full object-contain mt-5"/>)}</section>)}
            {/* Legacy sample pages are retained for older books. */}
            {(book.sample_pages || []).map((sp, i) => (
              <div key={i} className="mt-6 k-book-page">
                <p className="k-overline mb-3">Sample page · {sp.page_title}</p>
                <EditableText value={sp.body} editing={editing} setEditing={setEditing} section={`sample_page:${i}`} onSave={saveEdit}>
                  <p className="font-serif text-ink leading-relaxed whitespace-pre-wrap">{sp.body}</p>
                </EditableText>
              </div>
            ))}

            {/* Final message */}
            <div className="mt-6 k-book-page">
              <p className="k-overline mb-3">Final message</p>
              <EditableText value={book.final_message} editing={editing} setEditing={setEditing} section="final_message" onSave={saveEdit}>
                <p className="font-serif italic text-ink leading-relaxed whitespace-pre-wrap">{book.final_message}</p>
              </EditableText>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-80 lg:sticky lg:top-24 self-start space-y-5">
            <div className="k-card p-6">
              <p className="k-overline mb-3">Refine</p>
              <p className="text-sm text-inkMute leading-relaxed">Adjust the tone or add a missing detail and we'll re-write the draft.</p>
              <div className="mt-4 space-y-3">
                <div>
                  <p className="k-label">Tone override</p>
                  <Select value={toneOverride} onValueChange={setToneOverride}>
                    <SelectTrigger className="k-input" aria-label="Tone override" data-testid="tone-override"><SelectValue placeholder="Keep current" /></SelectTrigger>
                    <SelectContent>
                      {TONE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="k-label">Instruction</p>
                  <Textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} aria-label="Regeneration instruction" className="k-input min-h-[90px]" placeholder="e.g., make chapter 3 funnier, add more about her cooking" data-testid="instruction-input" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {["make more emotional","make funnier","make simpler","make shorter","make elegant"].map((q) => (
                    <button key={q} disabled={busy} onClick={() => regenerateAll(q)} className="k-chip hover:bg-chocolate hover:text-cream transition-colors" data-testid={`quick-${q}`}>{q}</button>
                  ))}
                </div>
                <button onClick={() => regenerateAll()} disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="regenerate-btn">
                  {busy ? "Working…" : (<><Sparkles size={14} className="mr-1.5" /> Regenerate draft</>)}
                </button>
              </div>
            </div>
            <button onClick={() => navigate(`/product/${projectId}`)} className="btn-primary w-full" data-testid="continue-to-product">
              Continue to product selection <ArrowRight size={16} className="ml-1.5" />
            </button>
            <button onClick={() => navigate(`/create/${projectId}`)} className="btn-ghost w-full" data-testid="back-to-questionnaire">
              Back to questionnaire
            </button>
          </div>
        </div>
      </main>

      {/* Edit modal */}
      <Dialog open={Boolean(editing)} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        {editing && <DialogContent className="w-[calc(100%-32px)] max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="book-edit-dialog">
            <DialogTitle>Edit your story</DialogTitle>
            <DialogDescription>Changes are saved to your written draft. This does not approve the book for printing.</DialogDescription>
            <Textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} aria-label="Edit draft text" className="k-input min-h-[180px]" data-testid="edit-textarea" />
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn-ghost" onClick={() => setEditing(null)} data-testid="cancel-book-edit">Cancel</button>
              <button className="btn-primary" onClick={saveEdit} data-testid="save-edit-btn"><Check size={14} className="mr-1.5" /> Save</button>
            </div>
        </DialogContent>}
      </Dialog>
    </div>
  );
}

function EditableHeader({ value, editing, setEditing, section, children, testId }) {
  return (
    <div className="group inline-block relative">
      {children}
      <button onClick={() => setEditing({ section, text: value || "" })} className="ml-2 inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-inkMute hover:text-chocolate" aria-label={`Edit ${section}`} data-testid={testId || `edit-${section}`}>
        <Pencil size={14} />
      </button>
    </div>
  );
}

function EditableText({ value, editing, setEditing, section, children }) {
  return (
    <div className="group relative">
      {children}
      <button onClick={() => setEditing({ section, text: value || "" })} className="inline-flex items-center gap-2 min-h-[44px] text-xs text-inkMute hover:text-chocolate" aria-label={`Edit ${section}`} data-testid={`edit-${section}`}>
        <Pencil size={14} />
        Edit
      </button>
    </div>
  );
}
